<template>
  <section class="nd-stat-grid">
    <div v-for="stat in stats" :key="stat.label" class="nd-stat-card">
      <span class="nd-stat-card__label">{{ stat.label }}</span>
      <strong class="nd-stat-card__value" :class="{ 'nd-stat-card__value--date': stat.date }">{{ stat.value }}</strong>
      <span class="nd-stat-card__hint">{{ stat.hint }}</span>
    </div>
  </section>
</template>

<script>
export default {
  name: "ModuleStatGrid",

  props: {
    module: {
      type: String,
      required: true,
    },
    items: {
      type: Array,
      default: () => [],
    },
    count: {
      type: Number,
      default: 0,
    },
  },

  computed: {
    rows() {
      return this.items || [];
    },
    stats() {
      const builders = {
        admins: this.adminStats,
        allergies: () => this.catalogStats("Alergias", "com nome", "sem nome", "base de anamnese"),
        appointments: this.appointmentStats,
        dentists: this.dentistStats,
        dsd: this.dsdStats,
        examTypes: () => this.catalogStats("Tipos ativos", "classificados", "sem nome", "fluxo de exames"),
        insurances: this.insuranceStats,
        laboratories: this.laboratoryStats,
        odontograms: this.odontogramStats,
        patients: this.patientStats,
        prescriptions: this.prescriptionStats,
        procedures: () => this.catalogStats("Procedimentos", "com nome", "sem nome", "tabela clínica"),
        proposals: this.proposalStats,
        roles: this.roleStats,
        specialties: () => this.catalogStats("Especialidades", "com nome", "sem nome", "agenda clínica"),
        tests: this.testStats,
      };

      const builder = builders[this.module] || (() => this.catalogStats("Cadastros", "com nome", "sem nome", "base operacional"));
      return builder();
    },
  },

  methods: {
    value(item, keys) {
      for (const key of keys) {
        const parts = key.split(".");
        let current = item;
        for (const part of parts) {
          current = current && current[part];
        }
        if (current !== undefined && current !== null && current !== "") return current;
      }
      return null;
    },
    has(item, keys) {
      return this.value(item, keys) !== null;
    },
    countWhere(predicate) {
      return this.rows.filter(predicate).length;
    },
    percent(value, total = this.rows.length) {
      if (!total) return "0%";
      return `${Math.round((value / total) * 100)}%`;
    },
    currency(value) {
      return Number(value || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
    },
    dateOnly(value) {
      if (!value) return null;
      return String(value).split("T")[0];
    },
    today() {
      return new Date().toISOString().split("T")[0];
    },
    statusText(item) {
      return String(this.value(item, ["status", "state"]) || "").toLowerCase();
    },
    catalogStats(totalLabel, namedHint, missingHint, operationHint) {
      const named = this.countWhere((item) => this.has(item, ["name", "title", "description"]));
      const missing = Math.max(this.rows.length - named, 0);
      return [
        { label: totalLabel, value: this.count || this.rows.length, hint: "disponíveis para uso" },
        { label: "Cadastro consistente", value: this.percent(named), hint: namedHint },
        { label: "Revisar cadastro", value: missing, hint: missingHint },
        { label: "Uso operacional", value: this.rows.length ? "Pronto" : "Vazio", hint: operationHint },
      ];
    },
    adminStats() {
      const withEmail = this.countWhere((item) => this.has(item, ["email"]));
      return [
        { label: "Usuários operacionais", value: this.count || this.rows.length, hint: "com acesso ao sistema" },
        { label: "Com e-mail", value: this.percent(withEmail), hint: "credencial identificável" },
        { label: "Sem e-mail", value: this.rows.length - withEmail, hint: "revisar acesso" },
        { label: "Gestão de acesso", value: this.rows.length ? "Ativa" : "Vazia", hint: "controle administrativo" },
      ];
    },
    dentistStats() {
      const withEmail = this.countWhere((item) => this.has(item, ["email"]));
      const withRole = this.countWhere((item) => this.has(item, ["role.name"]));
      return [
        { label: "Dentistas ativos", value: this.count || this.rows.length, hint: "com acesso ao sistema" },
        { label: "Com e-mail", value: this.percent(withEmail), hint: "credencial de acesso" },
        { label: "Com cargo clínico", value: this.percent(withRole), hint: "perfil de permissões" },
        { label: "Agenda clínica", value: this.rows.length ? "Habilitada" : "Vazia", hint: "pronta para atendimentos" },
      ];
    },
    appointmentStats() {
      const today = this.today();
      const todayCount = this.countWhere((item) => this.dateOnly(this.value(item, ["scheduledDate", "date"])) === today);
      const future = this.countWhere((item) => this.dateOnly(this.value(item, ["scheduledDate", "date"])) > today);
      const withoutPatient = this.countWhere((item) => !this.has(item, ["patient.name", "patientName"]));
      return [
        { label: "Consultas hoje", value: todayCount, hint: "agenda do dia" },
        { label: "Próximas consultas", value: future, hint: "a partir de amanhã" },
        { label: "Sem paciente", value: withoutPatient, hint: "cadastro a revisar" },
        { label: "Agenda monitorada", value: this.count || this.rows.length, hint: "itens no período" },
      ];
    },
    dsdStats() {
      const withPatient = this.countWhere((item) => this.has(item, ["patient.name", "patientName"]));
      const withImage = this.countWhere((item) => this.has(item, ["resource.url", "resource.path", "image", "resourceId"]));
      return [
        { label: "DSDs com paciente", value: withPatient, hint: "vínculo clínico" },
        { label: "Com imagem", value: withImage, hint: "recurso principal" },
        { label: "Sem imagem", value: this.rows.length - withImage, hint: "precisa completar" },
        { label: "Planejamentos", value: this.count || this.rows.length, hint: "casos cadastrados" },
      ];
    },
    insuranceStats() {
      const withRegistration = this.countWhere((item) => this.has(item, ["registration", "code", "document"]));
      return [
        { label: "Convênios ativos", value: this.count || this.rows.length, hint: "operadoras disponíveis" },
        { label: "Com identificação", value: this.percent(withRegistration), hint: "código ou registro" },
        { label: "A revisar", value: this.rows.length - withRegistration, hint: "sem identificador" },
        { label: "Base de faturamento", value: this.rows.length ? "Pronta" : "Vazia", hint: "uso em pacientes" },
      ];
    },
    laboratoryStats() {
      const withContact = this.countWhere((item) => this.has(item, ["phone", "email", "contact", "registration"]));
      return [
        { label: "Laboratórios", value: this.count || this.rows.length, hint: "rede de apoio" },
        { label: "Com contato", value: this.percent(withContact), hint: "telefone ou e-mail" },
        { label: "Sem contato", value: this.rows.length - withContact, hint: "revisar cadastro" },
        { label: "Fluxo de exames", value: this.rows.length ? "Disponível" : "Vazio", hint: "solicitações laboratoriais" },
      ];
    },
    odontogramStats() {
      const withPatient = this.countWhere((item) => this.has(item, ["patient.name", "patientName"]));
      const withTeeth = this.countWhere((item) => this.has(item, ["toothNumber", "toothStatus"]));
      return [
        { label: "Mapas com paciente", value: withPatient, hint: "vínculo clínico" },
        { label: "Com dentes marcados", value: withTeeth, hint: "odontograma preenchido" },
        { label: "Sem marcações", value: this.rows.length - withTeeth, hint: "precisa completar" },
        { label: "Mapas clínicos", value: this.count || this.rows.length, hint: "histórico odontológico" },
      ];
    },
    patientStats() {
      const withPhone = this.countWhere((item) => this.has(item, ["phone.value", "phone", "guardianPhone"]));
      const withCpf = this.countWhere((item) => this.has(item, ["registration", "cpf"]));
      const withGuardian = this.countWhere((item) => this.has(item, ["guardianName", "guardianCpf", "guardianPhone"]));
      return [
        { label: "Com telefone", value: this.percent(withPhone), hint: "contato de atendimento" },
        { label: "Com documento", value: this.percent(withCpf), hint: "CPF ou registro" },
        { label: "Responsáveis", value: withGuardian, hint: "menores ou dependentes" },
        { label: "A revisar", value: this.rows.length - Math.min(withPhone, withCpf), hint: "sem contato/documento" },
      ];
    },
    prescriptionStats() {
      const medicineTotal = this.rows.reduce((sum, item) => {
        const meds = this.value(item, ["medicines", "medications", "items"]);
        return sum + (Array.isArray(meds) ? meds.length : Number(this.value(item, ["medicineCount"]) || 0));
      }, 0);
      const withPatient = this.countWhere((item) => this.has(item, ["patient.name", "patientName"]));
      return [
        { label: "Com paciente", value: withPatient, hint: "prescrição vinculada" },
        { label: "Medicamentos", value: medicineTotal, hint: "itens prescritos" },
        { label: "Sem paciente", value: this.rows.length - withPatient, hint: "revisar emissão" },
        { label: "Prescrições", value: this.count || this.rows.length, hint: "documentos emitidos" },
      ];
    },
    proposalStats() {
      const total = this.rows.reduce((sum, item) => sum + Number(this.value(item, ["totalValue", "value"]) || 0), 0);
      const approved = this.countWhere((item) => Number(this.value(item, ["status"]) || 0) === 2);
      const pending = this.countWhere((item) => Number(this.value(item, ["status"]) || 0) === 1);
      return [
        { label: "Valor em propostas", value: this.currency(total), hint: "soma visível" },
        { label: "Aprovados", value: approved, hint: "orçamentos ganhos" },
        { label: "Pendentes", value: pending, hint: "aguardando resposta" },
        { label: "Ticket médio", value: this.currency(this.rows.length ? total / this.rows.length : 0), hint: "por proposta" },
      ];
    },
    roleStats() {
      const clinical = this.countWhere((item) => !!this.value(item, ["dentist"]));
      const admin = Math.max(this.rows.length - clinical, 0);
      return [
        { label: "Perfis de acesso", value: this.count || this.rows.length, hint: "papéis configurados" },
        { label: "Clínicos", value: clinical, hint: "dentistas/equipe assistencial" },
        { label: "Administrativos", value: admin, hint: "backoffice" },
        { label: "Governança", value: this.rows.length ? "Ativa" : "Vazia", hint: "permissões por cargo" },
      ];
    },
    testStats() {
      const pending = this.countWhere((item) => this.statusText(item).includes("pend") || Number(this.value(item, ["status"]) || 0) === 1);
      const done = this.countWhere((item) => this.statusText(item).includes("concl") || Number(this.value(item, ["status"]) || 0) === 2);
      const withLab = this.countWhere((item) => this.has(item, ["laboratory.name", "laboratory"]));
      return [
        { label: "Pendentes", value: pending, hint: "aguardam resultado" },
        { label: "Concluídos", value: done, hint: "resultado finalizado" },
        { label: "Com laboratório", value: this.percent(withLab), hint: "encaminhamento definido" },
        { label: "Solicitações", value: this.count || this.rows.length, hint: "exames em acompanhamento" },
      ];
    },
  },
};
</script>
